/* SUMMARY
  * Imports
  * useCustomerReceivables: read a customer's overdue Odoo invoices
*/

/* Imports */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../services/api/api";
import { MANAGER_ROLES, useUserRoles } from "./useUserRoles";
/***/

/**
 * useCustomerReceivables: read a customer's overdue Odoo invoices.
 *
 * Two screens show these figures — the customer detail and the intervention creation form — so the
 * fetch, the role gate and the three states live here once rather than being written twice.
 *
 * @param customerId - customer to read, absent while no customer is selected
 */
export function useCustomerReceivables(customerId?: string) {
  const { hasAnyRole } = useUserRoles();
  const allowed = hasAnyRole(MANAGER_ROLES);

  const [data, setData] = useState<EasyTech.CustomerReceivables | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  /*
   * The customer this hook is currently about. Every response is checked against it before being
   * stored: in the creation form a planner can switch customers faster than the network answers,
   * and the first response landing after the second would show one customer's debt under another's
   * name.
   */
  const current = useRef<string | undefined>(customerId);

  useEffect(() => {
    current.current = customerId;
    /*
     * A refresh in flight for the previous customer cannot clear this flag once it resolves — the
     * guard below on its own `finally` correctly declines, because by then `current.current` no
     * longer matches. Without this reset, that customer's refresh button would stay disabled
     * forever, with nothing left to un-disable it.
     */
    setRefreshing(false);
    /*
     * Cleared unconditionally, before either branch below. Otherwise, while the GET for a newly
     * selected customer is in flight, both body() and syncedAt() would keep rendering the PREVIOUS
     * customer's figures and date underneath the spinner — and if that GET then fails, the error
     * branch (which now shows stale figures alongside a "refresh failed" notice, see
     * CustomerReceivables) would present the old customer's numbers as if they belonged to the new
     * one.
     */
    setData(null);

    if (!allowed || !customerId) {
      setLoading(false);
      // Symmetric with the other three flags reset above/below: nothing else clears this for a
      // customer that has just been deselected, so it would otherwise stay stuck at its last value.
      setError(false);
      return;
    }

    setLoading(true);
    setError(false);

    /*
     * One flag per effect run rather than per customer id. A `current.current` ref comparison can
     * be fooled by A -> B -> A: A's first request's `.finally` sees `current.current === "A"` again
     * once the third switch lands, and clears `loading` even though A's *second* request is still
     * in flight. This closure-local flag is set only by THIS run's own cleanup, so a superseded run
     * can never be mistaken for current again.
     */
    let ignore = false;

    api.get(`users/customers/${customerId}/receivables`)
      .then((ret) => {
        if (ignore) return;
        setData(ret as EasyTech.CustomerReceivables);
      })
      .catch((err) => {
        if (ignore) return;
        console.error(err);
        setError(true);
      })
      .finally(() => {
        if (ignore) return;
        setLoading(false);
      });

    return () => { ignore = true; };
  }, [customerId, allowed]);

  /**
   * Ask the server to re-read this customer from Odoo now. Replaces the state from the response, so
   * there is no second GET. A failure leaves the previous figures in place on purpose.
   *
   * `refresh` is a callback, not an effect, so it has no cleanup function to flip an `ignore` flag
   * of its own. It compares against `current.current` instead — the ref the effect above keeps
   * pointed at whichever customer is current — so a response landing after the customer has since
   * changed is still discarded.
   */
  const refresh = useCallback(async () => {
    if (!allowed || !customerId) return;

    setRefreshing(true);
    setError(false);

    try {
      const ret = await api.post(`users/customers/${customerId}/receivables/refresh`, {});
      if (current.current !== customerId) return;
      setData(ret as EasyTech.CustomerReceivables);
    } catch (err) {
      console.error(err);
      if (current.current !== customerId) return;
      setError(true);
    } finally {
      if (current.current === customerId) setRefreshing(false);
    }
  }, [customerId, allowed]);

  return { data, loading, refreshing, error, refresh, allowed };
}
/***/
